import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, Briefcase, Pencil } from "lucide-react";
import { businessesApi } from "@/api/counterparties";
import { ApiError } from "@/api/client";
import type { BusinessDetailDto } from "@/types/counterparties";
import { COUNTERPARTY_PERMISSIONS } from "@/types/counterparties";
import {
  Badge,
  Button,
  DetailField as Field,
  DetailFieldList,
  RECORD_CARD_MIN_WIDTH,
  RECORD_RAIL_WIDTH,
  ResponsiveGrid,
  ResponsiveSplit,
} from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { useAuth } from "@/hooks/useAuth";
import { useDialogs } from "@/hooks/useDialogs";
import { useToast } from "@/components/ui/Toast";
import { CounterpartyStatusBadge } from "@/components/counterparties/StatusBadge";
import { RequestDocumentsButton } from "@/components/Collection/RequestDocumentsButton";
import { CounterpartyDealsTab } from "@/components/counterparties/CounterpartyDealsTab";
import { ActiveDealsBlockedDialog } from "@/components/counterparties/ActiveDealsBlockedDialog";
import { DocumentPanel } from "@/pages/documents/components/DocumentPanel";
import { TaskListMini } from "@/pages/tasks/components/TaskListMini";
import { AppointmentListMini } from "@/pages/calendar/components/AppointmentListMini";
import { CommunicationsTabPanel } from "@/pages/communications/components/CommunicationsTabPanel";
import { NotesTabPanel } from "@/pages/notes/components/NotesTabPanel";
import { ActivityTabPanel } from "@/pages/activity/components/ActivityTabPanel";
import { RelatedConversationsPanel } from "@/components/messaging/RelatedConversationsPanel";
import { OverviewActivityPreview } from "@/components/activity/OverviewActivityPreview";
import { useOpenTaskCountByRelated } from "@/pages/tasks/hooks/useWorkTasks";
import { useCommunicationsRecentCount } from "@/pages/communications/hooks/useCommunications";
import { useNotesRecentCount } from "@/pages/notes/hooks/useNotes";
import { useActivityRecentCount } from "@/pages/activity/hooks/useActivityByOwner";
import {
  CROSS_CUTTING_TAB_ICONS,
  EntityTabBar,
  type EntityTabItem,
} from "@/components/layout/EntityTabBar";
import { businessKeys, invalidateCounterpartyReads } from "../counterpartyKeys";

const PERMS = COUNTERPARTY_PERMISSIONS.Business;

// Layout Compliance v1.0 PR 9 — Business is the deal-subject counterparty
// (what's being bought/sold). Deals tab is entity-specific and keeps its
// position before the cross-cutting block per Layout Standard §3.
type TabId =
  | "overview"
  | "deals"
  | "documents"
  | "tasks"
  | "calendar"
  | "messages"
  | "communications"
  | "notes"
  | "activity";

const TABS: readonly TabId[] = [
  "overview",
  "deals",
  "documents",
  "tasks",
  "calendar",
  "messages",
  "communications",
  "notes",
  "activity",
] as const;

const TAB_ITEMS: ReadonlyArray<EntityTabItem<TabId>> = [
  { key: "overview", label: "Overview", icon: CROSS_CUTTING_TAB_ICONS.overview },
  { key: "deals", label: "Deals", icon: Briefcase },
  { key: "documents", label: "Documents", icon: CROSS_CUTTING_TAB_ICONS.documents },
  { key: "tasks", label: "Tasks", icon: CROSS_CUTTING_TAB_ICONS.tasks },
  { key: "calendar", label: "Calendar", icon: CROSS_CUTTING_TAB_ICONS.calendar },
  { key: "messages", label: "Messages", icon: CROSS_CUTTING_TAB_ICONS.messages },
  { key: "communications", label: "Communications", icon: CROSS_CUTTING_TAB_ICONS.communications },
  { key: "notes", label: "Notes", icon: CROSS_CUTTING_TAB_ICONS.notes },
  { key: "activity", label: "Activity", icon: CROSS_CUTTING_TAB_ICONS.activity },
] as const;

function isTabId(value: string | null): value is TabId {
  return value !== null && (TABS as readonly string[]).includes(value);
}

export function BusinessDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dialogs = useDialogs();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const [archiveBlocked, setArchiveBlocked] = useState(false);

  const [params, setParams] = useSearchParams();
  const tabParam = params.get("tab");
  const tab: TabId = isTabId(tabParam) ? tabParam : "overview";

  const setTab = useCallback(
    (next: TabId) => {
      const updated = new URLSearchParams(params);
      if (next === "overview") updated.delete("tab");
      else updated.set("tab", next);
      setParams(updated, { replace: true });
    },
    [params, setParams],
  );

  useEffect(() => {
    function isTypingTarget(t: EventTarget | null): boolean {
      if (!(t instanceof HTMLElement)) return false;
      if (t.isContentEditable) return true;
      const tag = t.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
    }
    function handler(e: KeyboardEvent) {
      if (isTypingTarget(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const idx = Number(e.key);
      if (Number.isInteger(idx) && idx >= 1 && idx <= TABS.length) {
        e.preventDefault();
        setTab(TABS[idx - 1]);
      }
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [setTab]);

  const canUpdate = hasPermission(PERMS.Update);
  const canDelete = hasPermission(PERMS.Delete);

  const businessQuery = useQuery({
    queryKey: businessKeys.detail(id),
    queryFn: () => businessesApi.get(id!),
    enabled: !!id,
  });

  useBreadcrumbs(
    businessQuery.data
      ? [
          { label: "Businesses", href: "/counterparties/businesses" },
          { label: businessQuery.data.legalEntityName },
        ]
      : null,
  );

  const archive = useMutation({
    mutationFn: () => businessesApi.archive(id!),
    onSuccess: () => {
      invalidateCounterpartyReads(queryClient, "business");
      toast.success("Business archived.");
      navigate("/counterparties/businesses");
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409) {
        setArchiveBlocked(true);
      } else {
        toast.error(e instanceof Error ? e.message : "Archive failed.");
      }
    },
  });

  const openTaskCountQuery = useOpenTaskCountByRelated("Business", id ?? undefined);
  const openTaskCount = openTaskCountQuery.data ?? 0;
  const commsRecent = useCommunicationsRecentCount("business", id ?? "", 30);
  const notesRecent = useNotesRecentCount("business", id ?? "", 7);
  const activityRecent = useActivityRecentCount("business", id ?? "", 7);

  if (businessQuery.isLoading) {
    return <div className="text-caption uppercase tracking-wide text-gray-mid">Loading…</div>;
  }
  if (businessQuery.isError || !businessQuery.data) {
    return (
      <div className="text-small text-status-error">
        {(businessQuery.error as Error)?.message ?? "Business not found."}
      </div>
    );
  }

  const biz = businessQuery.data;

  const onArchive = async () => {
    const ok = await dialogs.confirm({
      title: `Archive ${biz.legalEntityName}?`,
      message: "This business will no longer appear in active lists.",
      confirmLabel: "Archive",
      destructive: true,
    });
    if (!ok) return;
    archive.mutate();
  };

  const tabBadge = (key: TabId): string | number | null => {
    switch (key) {
      case "tasks":
        return openTaskCount > 0 ? openTaskCount : null;
      case "communications":
        return commsRecent.data && commsRecent.data > 0 ? commsRecent.data : null;
      case "notes":
        return notesRecent.data && notesRecent.data > 0 ? notesRecent.data : null;
      case "activity":
        return activityRecent.data && activityRecent.data > 0 ? activityRecent.data : null;
      default:
        return null;
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Counterparties · Business"
        title={biz.legalEntityName}
        actions={
          <>
            <RequestDocumentsButton
              ownerEntityType="Business"
              ownerEntityId={biz.id}
              ownerDisplayName={biz.legalEntityName}
              size="sm"
            />
            {canUpdate && (
              <Button size="sm" variant="secondary" onClick={() => navigate(`/counterparties/businesses/${biz.id}/edit`)}>
                <Pencil size={14} aria-hidden /> Edit
              </Button>
            )}
            {canDelete && biz.status === "Active" && (
              <Button size="sm" variant="destructive" onClick={onArchive} disabled={archive.isPending}>
                <Archive size={14} aria-hidden /> Archive
              </Button>
            )}
          </>
        }
      />

      <div className="flex items-center gap-space-3 -mt-space-3 mb-space-4">
        <CounterpartyStatusBadge status={biz.status} />
        <Badge status="info">{biz.entityType}</Badge>
        <span className="text-caption text-gray-mid">
          NAICS {biz.naicsCode} · HQ {biz.hqAddress.city}, {biz.hqAddress.state}
        </span>
      </div>

      <EntityTabBar
        ariaLabel="Business sections"
        testIdPrefix="business"
        items={TAB_ITEMS.map((it) => ({ ...it, badge: tabBadge(it.key) }))}
        value={tab}
        onChange={setTab}
      />

      <div className="pt-space-4">
        {tab === "overview" && (
          // Container-driven — Table & Tile Standard §12. A `lg:` breakpoint
          // asks the WINDOW how wide this box is, which is the one thing the
          // window cannot answer once the box is a tab body or a drawer.
          <ResponsiveSplit
            gap="md"
            mainMinWidth="34rem"
            railWidth={RECORD_RAIL_WIDTH}
            railGrows={false}
            main={<BusinessOverview business={biz} />}
            rail={
              <OverviewActivityPreview
                ownerType="business"
                ownerId={biz.id}
                detailPath={`/counterparties/businesses/${biz.id}`}
              />
            }
          />
        )}
        {tab === "deals" && <CounterpartyDealsTab kind="Business" businessId={biz.id} />}
        {tab === "documents" && (
          <DocumentPanel ownerType="Business" ownerId={biz.id} rootName={biz.legalEntityName} />
        )}
        {tab === "tasks" && (
          <TaskListMini
            relatedEntityType="Business"
            relatedEntityId={biz.id}
            addButtonLabel="+ Add task linked to this business"
          />
        )}
        {tab === "calendar" && (
          <AppointmentListMini
            relatedEntityType="Business"
            relatedEntityId={biz.id}
            entityLabel={biz.legalEntityName}
          />
        )}
        {tab === "messages" && (
          <RelatedConversationsPanel
            resourceType="business"
            resourceId={biz.id}
            entityLabel={biz.legalEntityName}
          />
        )}
        {tab === "communications" && (
          <CommunicationsTabPanel
            ownerType="business"
            ownerId={biz.id}
            ownerLabel={biz.legalEntityName}
          />
        )}
        {tab === "notes" && <NotesTabPanel ownerType="business" ownerId={biz.id} />}
        {tab === "activity" && <ActivityTabPanel ownerType="business" ownerId={biz.id} />}
      </div>

      {archiveBlocked && (
        <ActiveDealsBlockedDialog
          open
          displayName={biz.legalEntityName}
          onClose={() => setArchiveBlocked(false)}
        />
      )}
    </>
  );
}

function BusinessOverview({ business: b }: { business: BusinessDetailDto }) {
  return (
    <ResponsiveGrid cols={2} minColWidth={RECORD_CARD_MIN_WIDTH} gap="md">
      <Card title="Identity">
        <DetailFieldList>
          <Field label="Legal name">{b.legalEntityName}</Field>
          <Field label="DBA">{b.dba ?? "—"}</Field>
          <Field label="Type">{b.entityType}</Field>
          <Field label="State of formation">{b.stateOfFormation}</Field>
          <Field label="Date of formation">{b.dateOfFormation ?? "—"}</Field>
          <Field label="EIN"><span className="font-mono">{b.einMasked ?? "—"}</span></Field>
          <Field label="Website">{b.website ?? "—"}</Field>
        </DetailFieldList>
      </Card>

      <Card title="Industry & operations">
        <DetailFieldList>
          <Field label="NAICS">{b.naicsCode}</Field>
          <Field label="Industry">{b.industryDescription}</Field>
          <Field label="Years operating (calculated)">{b.yearsOperating ?? "—"}</Field>
          <Field label="Years in operation (broker-reported)">{b.yearsInOperation ?? "—"}</Field>
          <Field label="# locations">{b.numberOfLocations ?? "—"}</Field>
          <Field label="# employees">{b.numberOfEmployees ?? "—"}</Field>
          <Field label="Annual revenue">{formatMoney(b.annualRevenuePreliminary)}</Field>
          <Field label="Annual EBITDA">{formatMoney(b.annualEBITDAPreliminary)}</Field>
          <Field label="Recurring revenue %">{b.recurringRevenuePct == null ? "—" : `${b.recurringRevenuePct}%`}</Field>
          <Field label="Top-5 customer concentration %">{b.customerConcentrationTop5Pct == null ? "—" : `${b.customerConcentrationTop5Pct}%`}</Field>
          <Field label="Lease or own">{b.leaseOrOwn ?? "—"}</Field>
          <Field label="Lease expiration">{b.leaseExpirationDate ?? "—"}</Field>
        </DetailFieldList>
      </Card>

      <Card title="HQ address">
        <AddressBlock label="HQ" address={b.hqAddress} />
      </Card>

      <Card title="Description & notes">
        <p className="text-small text-onyx whitespace-pre-line">
          {b.briefDescription || <span className="text-gray-mid">No description.</span>}
        </p>
        <div className="mt-space-3 text-small">
          <div className="text-caption uppercase tracking-wide text-gray-mid mb-space-1">Tags</div>
          <div className="flex flex-wrap gap-space-1">
            {b.tags.length === 0 ? (
              <span className="text-caption text-gray-mid">No tags</span>
            ) : (
              b.tags.map((t) => <Badge key={t} status="gold">{t}</Badge>)
            )}
          </div>
        </div>
      </Card>
    </ResponsiveGrid>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-white-pure border border-border-light rounded-lg p-space-4">
      <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow mb-space-3 m-0">
        {title}
      </h3>
      <div className="grid grid-cols-1 gap-space-2">{children}</div>
    </section>
  );
}

function AddressBlock({ label, address }: { label: string; address: { streetAddress1: string; streetAddress2?: string | null; city: string; state: string; zip: string; country: string } }) {
  return (
    <div className="text-small">
      <div className="text-caption uppercase tracking-wide text-gray-mid mb-space-1">{label}</div>
      <div className="text-onyx">{address.streetAddress1}</div>
      {address.streetAddress2 && <div className="text-onyx">{address.streetAddress2}</div>}
      <div className="text-onyx">{address.city}, {address.state} {address.zip}</div>
      <div className="text-gray-mid">{address.country}</div>
    </div>
  );
}

function formatMoney(value: number | null | undefined): string {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);
}
